import React, { useEffect, useState, useRef, memo, useCallback } from "react";
import MiniTitle from "layouts/MiniTitle";
import MainTitle from "layouts/MainTitle";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import { motion, useInView, AnimatePresence } from "framer-motion";
import "swiper/css";
import "swiper/css/pagination";
import "styles/public.css";
import axios from "axios";
import { RxCrossCircled } from "react-icons/rx";

const BASE_URL = "https://algoritm-api-edu.uz/";
const API_URL = `${BASE_URL}api/public/MainPageUi/GetAllTestimonials`;

const TestimonialCard = memo(({ item, onReadMore, onPlayVideo }) => {
  const words = item.message?.trim().split(/\s+/) || [];
  const isLong = words.length > 60;
  const shortText = isLong
    ? words.slice(0, 55).join(" ") + "..."
    : item.message;

  return (
    <motion.div
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.7 }}
      whileHover={{ y: -8 }}
      className="h-full"
    >
      <div className="testimonial-card">
        <div className="testimonial-header">
          <img
            src={BASE_URL + item.photoUrl}
            alt={item.studentName}
            className="testimonial-img"
            loading="lazy"
            onError={(e) => {
              e.target.src = "https://via.placeholder.com/80?text=Student";
            }}
          />
          <p className="testimonial-name">{item.studentName || "Talaba"}</p>
        </div>

        <p className="testimonial-message short">{shortText}</p>

        <div className="testimonial-footer">
          {isLong && (
            <button onClick={() => onReadMore(item)} className="more-btn">
              Toʻliq oʻqish
            </button>
          )}

          {item.videoUrl && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="play-btn"
              onClick={() => onPlayVideo(item.videoUrl)}
            >
              Videoni koʻrish
            </motion.button>
          )}
        </div>
      </div>
    </motion.div>
  );
});
TestimonialCard.displayName = "TestimonialCard";

const VideoModal = memo(({ videoUrl, onClose }) => {
  if (!videoUrl) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="video-modal show"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9 }}
          animate={{ scale: 1 }}
          exit={{ scale: 0.9 }}
          className="video-modal-content"
          onClick={(e) => e.stopPropagation()}
        >
          <button className="close-btn" onClick={onClose} aria-label="Yopish">
            <RxCrossCircled size={30} />
          </button>
          <video
            src={BASE_URL + videoUrl}
            controls
            autoPlay
            muted={false}
            className="video-player"
            playsInline
          />
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
});
VideoModal.displayName = "VideoModal";

const TextModal = memo(({ item, onClose, onPlayVideo }) => {
  if (!item) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="text-modal-overlay"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.92, y: 30 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.92, y: 30 }}
          transition={{ type: "spring", damping: 28, stiffness: 400 }}
          className="text-modal"
          onClick={(e) => e.stopPropagation()}
        >
          <button className="modal-close-corner" onClick={onClose}>
            ×
          </button>

          <div className="text-modal-content">
            <div className="testimonial-header">
              <img
                src={BASE_URL + item.photoUrl}
                alt={item.studentName}
                className="testimonial-img"
                loading="lazy"
              />
              <p className="testimonial-name">{item.studentName}</p>
            </div>

            <p className="testimonial-message full">{item.message}</p>

            {item.videoUrl && (
              <motion.button
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.96 }}
                className="play-btn"
                style={{ marginTop: "28px" }}
                onClick={() => {
                  onPlayVideo(item.videoUrl);
                  onClose();
                }}
              >
                Videoni koʻrish
              </motion.button>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
});
TextModal.displayName = "TextModal";

const Testimonials = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [videoUrl, setVideoUrl] = useState(null);
  const [modalItem, setModalItem] = useState(null);

  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, margin: "-100px" });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.post(API_URL, {
          search: "",
          sortBy: "",
          orderType: "",
          pageSize: 20,
          page: 1,
        });
        setItems(res.data.rows || []);
      } catch (err) {
        console.error("Fikrlarni yuklashda xato:", err);
        setItems([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handlePlayVideo = useCallback((url) => {
    setVideoUrl(url);
    document.body.style.overflow = "hidden";
  }, []);

  const handleCloseVideo = useCallback(() => {
    setVideoUrl(null);
    document.body.style.overflow = "unset";
  }, []);

  const handleReadMore = useCallback((item) => {
    setModalItem(item);
    document.body.style.overflow = "hidden";
  }, []);

  const handleCloseTextModal = useCallback(() => {
    setModalItem(null);
    document.body.style.overflow = "unset";
  }, []);

  return (
    <div className="testimonials-container" id="comments" ref={containerRef}>
      <motion.div
        initial={{ opacity: 0, y: -30 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.7 }}
      >
        <MiniTitle>Fikrlar</MiniTitle>
        <MainTitle>O’quvchilar fikrlari</MainTitle>
      </motion.div>

      <VideoModal videoUrl={videoUrl} onClose={handleCloseVideo} />

      <TextModal
        item={modalItem}
        onClose={handleCloseTextModal}
        onPlayVideo={handlePlayVideo}
      />

      {loading ? (
        <div className="skeleton-wrapper">
          {[1, 2, 3].map((n) => (
            <motion.div
              key={n}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: n * 0.15 }}
              className="skeleton-card"
            />
          ))}
        </div>
      ) : items.length === 0 ? (
        <p className="empty-state">Hozircha fikrlar mavjud emas</p>
      ) : (
        <Swiper
          modules={[Autoplay, Pagination]}
          spaceBetween={30}
          slidesPerView={3}
          centeredSlides={false}
          loop={true}
          autoplay={{
            delay: 3000,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          }}
          pagination={{ clickable: true, dynamicBullets: true }}
          speed={800}
          breakpoints={{
            320: { slidesPerView: 1.2, spaceBetween: 20 },
            640: { slidesPerView: 2, spaceBetween: 20 },
            768: { slidesPerView: 2.5 },
            1024: { slidesPerView: 3.2 },
            1280: { slidesPerView: 3.5 },
          }}
          className="testimonials-swiper"
        >
          {items.map((item, index) => (
            <SwiperSlide key={item.id || index}>
              <TestimonialCard
                item={item}
                onReadMore={handleReadMore}
                onPlayVideo={handlePlayVideo}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      )}
    </div>
  );
};

export default Testimonials;
